/**
 * A guard against the second press of a button whose first press is still working.
 *
 * Two of them, and both were paid for:
 *
 * - **Join.** Without one, the second tap of a slow "Join" started a *second* recorder --
 *   another microphone stream and another interval -- over the top of the first, which was
 *   then unreachable and never stopped.
 * - **Stop.** Pressing it again is routine rather than exceptional: people press it again
 *   when the first attempt reports an error. The first press tears the recorder down, so a
 *   naive second press read `recorder.seconds` off null and reported "Could not finish:
 *   null is not an object", burying whatever the real first error was.
 *
 * A latch rather than a boolean per site because the shape is identical and the reason is
 * one reason. It is deliberately *not* a React state: the guard has to hold on the tap that
 * arrives before the next render, which is exactly the tap it exists for.
 */
export class Latch {
  private held = false;

  /** True if this caller got in. False means somebody else is already inside. */
  tryEnter(): boolean {
    if (this.held) return false;
    this.held = true;
    return true;
  }

  release(): void {
    this.held = false;
  }

  get busy(): boolean {
    return this.held;
  }

  /** Run `work` if nothing else holds the latch, releasing it however `work` ends. */
  async run<T>(work: () => Promise<T>): Promise<T | undefined> {
    if (!this.tryEnter()) return undefined;
    try {
      return await work();
    } finally {
      this.release();
    }
  }
}
